/**
 * Quando uma cobrança ainda pode ser EDITADA.
 *
 * Espelha `apps/backend/src/libs/common/billing-settlement/billing-editable-status.ts`
 * — o backend é quem recusa de verdade; aqui só evitamos oferecer um botão que abriria
 * um formulário impossível de salvar. Mantenha os dois em sincronia.
 *
 * Depois que dinheiro entrou (ou saiu), ou depois que a cobrança morreu, editar deixa
 * de ser edição e vira reescrita de um fato: o extrato, o repasse e o comprovante que o
 * cliente já tem passam a discordar do registro. O caminho é estorno, cancelamento com
 * nova cobrança, ou cobrança complementar.
 */

/**
 * Status em que a cobrança NÃO aceita mais edição. DENYLIST de propósito: status novo
 * nasce editável, e o erro cai do lado de deixar o operador trabalhar.
 *
 * Seguem editáveis: `PENDING`, `OVERDUE` (viva, só vencida — é onde se renegocia) e
 * `IN_DISPUTE`.
 */
export const UNEDITABLE_BILLING_STATUSES = [
    'PAID',
    'PARTIALLY_PAID',
    'REFUNDED',
    'PARTIALLY_REFUNDED',
    'CANCELLED',
    'WRITTEN_OFF',
]

export function isEditableBillingStatus(status?: string | null): boolean {
    return !(!!status && UNEDITABLE_BILLING_STATUSES.includes(status))
}

/** Por que não dá para editar, com o caminho alternativo. `null` = pode editar. */
export function uneditableBillingReason(status?: string | null): string | null {
    switch (status) {
        case 'PAID':
            return 'Esta cobrança já foi paga e não pode mais ser editada. Para corrigir o valor, estorne e gere uma nova cobrança.'
        case 'PARTIALLY_PAID':
            return 'Esta cobrança já recebeu pagamento parcial e não pode mais ser editada. O saldo se ajusta por cobrança complementar ou estorno.'
        case 'REFUNDED':
        case 'PARTIALLY_REFUNDED':
            return 'Esta cobrança foi estornada e não pode mais ser editada. Gere uma nova cobrança para o que ainda for devido.'
        case 'CANCELLED':
            return 'Esta cobrança está cancelada e não pode ser editada. Gere uma nova cobrança.'
        case 'WRITTEN_OFF':
            return 'Esta cobrança foi baixada como perda e não pode ser editada.'
        default:
            return null
    }
}
